import { useEffect, useMemo } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { mutate } from "swr";
import { FormErrorMessage } from "../../components";
import { ApiSchemaVendorSipHeaderModel } from "../../models";
import { PaginationSettings } from "../../configs";
import { useFunctionalityVendorSipHeader } from "../../pages/hooks";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  vendorSipHeaderUrl: string | null;
  dataToEdit?: ApiSchemaVendorSipHeaderModel;
  setDataToEdit?: (value?: ApiSchemaVendorSipHeaderModel) => void;
};

export function ModalAddSipHeader(props: Props): JSX.Element {
  const {
    vendorSipHeaderList,
    availableHeaderTokens,
    getAllSipHeadersUrl,
    onAddSipHeader,
    onUpdateSipHeader,
  } = useFunctionalityVendorSipHeader({
    shouldVendorSipHeaderGetAllInvoke: true,
    pageSize: PaginationSettings.initialPageSize,
    pageNo: PaginationSettings.initialPageIndex,
    setEditModalShow: props.handleShow,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<ApiSchemaVendorSipHeaderModel>({
    defaultValues: {
      headerName: "",
      headerValue: "",
      valueType: 0,
    },
  });

  const isLoadingHeaders = vendorSipHeaderList === undefined;
  const { dataToEdit, setDataToEdit } = props;
  const valueType = Number(watch("valueType") ?? 0) as 0 | 1;
  const headerValue = watch("headerValue");
  const isDynamic = valueType === 0;

  useEffect(() => {
    if (dataToEdit) {
      reset({
        headerName: dataToEdit.headerName,
        headerValue: dataToEdit.headerValue,
        valueType: dataToEdit.valueType ?? 0,
      });
      return;
    }

    reset({
      headerName: "",
      headerValue: "",
      valueType: 0,
    });
  }, [dataToEdit, reset]);

  const usedDynamicHeaderValues = useMemo(() => {
    const arr = vendorSipHeaderList ?? [];
    const current = (dataToEdit?.headerValue ?? "").toLowerCase().trim();
    const currentValueType = dataToEdit?.valueType ?? 0;

    return new Set(
      arr
        .filter(h => (h?.valueType ?? 0) === 0)
        .map(h => (h?.headerValue ?? "").toLowerCase().trim())
        .filter(v => v && !(currentValueType === 0 && v === current))
    );
  }, [vendorSipHeaderList, dataToEdit?.headerValue, dataToEdit?.valueType]);

  const availableDynamicTokens = useMemo(
    () =>
      availableHeaderTokens.filter(
        token => !usedDynamicHeaderValues.has(token.token.toLowerCase())
      ),
    [availableHeaderTokens, usedDynamicHeaderValues]
  );

  useEffect(() => {
    if (!isDynamic) return;

    const currentValue = (headerValue ?? "").trim().toLowerCase();
    if (!currentValue) return;

    const isValidToken = availableDynamicTokens.some(
      token => token.token.toLowerCase() === currentValue
    );

    if (!isValidToken) {
      setValue("headerValue", "");
    }
  }, [availableDynamicTokens, headerValue, isDynamic, setValue]);

  const onCreateHandler = (form: ApiSchemaVendorSipHeaderModel) => {
    onAddSipHeader(form)
      .then(() => {
        mutate(props.vendorSipHeaderUrl || getAllSipHeadersUrl);
        setDataToEdit?.(undefined);
        props.handleShow(false);
      })
      .catch((e: any) => console.error(e));
  };

  const onEditHandler = async (form: ApiSchemaVendorSipHeaderModel) => {
    if (!dataToEdit) return;

    try {
      await onUpdateSipHeader(dataToEdit.id, {
        headerName: form.headerName,
        headerValue: form.headerValue,
        valueType: form.valueType,
      });
      setDataToEdit?.(undefined);
      props.handleShow(false);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Modal
      show={props.show}
      onHide={() => {
        setDataToEdit?.(undefined);
        props.handleShow(false);
      }}
      centered
      id="addVendorSipHeaderModal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{dataToEdit ? "Edit SIP Header" : "Add SIP Header"}</Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit(dataToEdit ? onEditHandler : onCreateHandler)}>
        <Modal.Body>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label className="position-relative required">Header Name</label>
                <small className="form-text text-muted mt-0 mb-1">
                  Do not add the x- prefix. It will be added automatically by the system.
                </small>
                <input
                  type="text"
                  {...register("headerName", {
                    required: true,
                    validate: v =>
                      (v?.trim().length ?? 0) > 0 || "Header name cannot be blank",
                  })}
                  className="form-control"
                  placeholder="Vendor Header Name"
                />
                {errors.headerName && <FormErrorMessage message="required" />}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label className="position-relative required">Value Type</label>
                <select
                  className={`form-control ${errors.valueType ? "is-invalid" : ""}`}
                  {...register("valueType", {
                    required: "Value type is required",
                    setValueAs: value => Number(value),
                    validate: value =>
                      value === 0 || value === 1 || "Value type is required",
                  })}
                >
                  <option value={0}>Dynamic</option>
                  <option value={1}>Literal</option>
                </select>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label className="position-relative required">Header Value</label>

                {isDynamic && isLoadingHeaders && (
                  <div className="d-flex align-items-center gap-2 mb-2" aria-live="polite">
                    <Spinner animation="border" size="sm" />
                    <span className="text-muted small ml-2">Loading header tokens...</span>
                  </div>
                )}

                {isDynamic ? (
                  <>
                    <select
                      id="headerValue"
                      className={`form-control ${errors.headerValue ? "is-invalid" : ""}`}
                      {...register("headerValue", {
                        required: "Header value is required",
                        validate: v => {
                          const key = (v ?? "").toLowerCase().trim();
                          if (!key) return "Header value cannot be blank";
                          return (
                            !usedDynamicHeaderValues.has(key) ||
                            "This header value is already in use"
                          );
                        },
                      })}
                      disabled={isLoadingHeaders || availableDynamicTokens.length === 0}
                    >
                      <option value="" disabled>
                        -- Select a header token --
                      </option>

                      {availableDynamicTokens.map(token => (
                        <option key={token.token} value={token.token}>
                          {token.token} - {token.description}
                        </option>
                      ))}
                    </select>

                    {!isLoadingHeaders && availableDynamicTokens.length === 0 && (
                      <small className="text-muted">
                        All available dynamic header tokens are already in use for this vendor.
                      </small>
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    className={`form-control ${errors.headerValue ? "is-invalid" : ""}`}
                    placeholder="Enter a literal header value"
                    {...register("headerValue", {
                      required: "Header value is required",
                      validate: v =>
                        (v?.trim().length ?? 0) > 0 || "Header value cannot be blank",
                    })}
                  />
                )}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link text-site text-uppercase"
            onClick={() => {
              setDataToEdit?.(undefined);
              props.handleShow(false);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary text-uppercase login-from-submit-btn"
            disabled={isDynamic && (isLoadingHeaders || availableDynamicTokens.length === 0)}
          >
            {dataToEdit ? "Save" : "Create"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
